import { type FC, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import {
  type UserDocumentPosition,
  type UserDocumentPositionERx,
} from '@redteclab/api/clients/bully'
import { IconArrowLeft } from '@redteclab/icons'

import { Button } from 'base-ui'

import { type OpenableControls } from '../../../../../generic-hooks'
import {
  userSubOrderAccessorIsProductErx,
  userSubOrderDocumentPositionAccessorGetTotalPrice,
} from '../../../../../order'
import { type DirectContactFormValues } from '../../DirectContactFormModel'

import { DirectContactFormOrderProductItemSelection } from './DirectContactFormOrderProductItemSelection'

type OrderProduct = UserDocumentPosition | UserDocumentPositionERx

const getProductKey = (product: OrderProduct, index: number): string => {
  const isERx = userSubOrderAccessorIsProductErx(product)

  if (isERx) {
    if (product.hashedToken) {
      return `erx:token:${product.hashedToken}`
    }

    return product.lineNum === undefined
      ? `erx:index:${index}`
      : `erx:line:${product.lineNum}`
  }

  if (product.offerId) {
    return `offer:id:${product.offerId}`
  }

  return product.lineNum === undefined
    ? `offer:index:${index}`
    : `offer:line:${product.lineNum}`
}

export const DirectContactFormOrderProductItemsSelection: FC<{
  modalControls: OpenableControls
  onBack: () => void
  products: OrderProduct[]
}> = ({ modalControls, onBack, products }) => {
  const { getValues, setValue } = useFormContext<DirectContactFormValues>()
  const selectableProductEntries = products.flatMap((product, index) =>
    product.itemName && product.pzn
      ? // eslint-disable-next-line unicorn/no-unnecessary-array-flat-map -- don't reference, refactor if touched
        [{ key: getProductKey(product, index), product }]
      : [],
  )
  const [selectedProductKeys, setSelectedProductKeys] = useState<Set<string>>(
    () => new Set(getValues('selectedProducts').map(({ key }) => key)),
  )
  const selectedProductEntries = selectableProductEntries.filter(({ key }) =>
    selectedProductKeys.has(key),
  )

  const toggleProduct = (productKey: string): void => {
    setSelectedProductKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys)

      if (nextKeys.has(productKey)) {
        nextKeys.delete(productKey)
      } else {
        nextKeys.add(productKey)
      }

      return nextKeys
    })
  }

  return (
    <>
      <Button
        className="mb-4 mt-1 items-start justify-start text-s font-medium text-dark-brand tablet:flex"
        data-qa-id="direct-contact-form-back-button-to-card-items-selection"
        onClick={onBack}
        startIcon={<IconArrowLeft />}
        type="button"
        variant="text"
      >
        <span className="text-dark-primary-max">
          <FormattedMessage id="direct.contact.form.back.to.order.selection" />
        </span>
      </Button>

      <div className="mt-auto flex h-full flex-col">
        <div className="flex flex-col gap-2">
          {selectableProductEntries.map(({ key, product }) => (
            <DirectContactFormOrderProductItemSelection
              checked={selectedProductKeys.has(key)}
              key={key}
              onToggle={() => {
                toggleProduct(key)
              }}
              product={product}
            />
          ))}
        </div>

        {selectedProductEntries.length > 0 ? (
          <Button
            className="mb-2 mt-6 w-full tablet:mt-16"
            data-qa-id="direct-contact-form-confirm-items-selection"
            onClick={() => {
              const selectedProducts = selectedProductEntries.map(
                ({ key, product }) => ({
                  itemName: product.itemName ?? '',
                  key,
                  pzn: product.pzn ?? '',
                  totalCost:
                    userSubOrderDocumentPositionAccessorGetTotalPrice(
                      product,
                    ) ?? 0,
                }),
              )

              setValue(
                'productCode',
                selectedProducts.map(({ pzn }) => pzn).join(', '),
                { shouldDirty: true, shouldValidate: true },
              )
              setValue('selectedProducts', selectedProducts, {
                shouldDirty: true,
              })
              modalControls.handleClose()
            }}
            type="button"
          >
            <FormattedMessage id="direct.contact.form.confirm.selection" />
          </Button>
        ) : null}
      </div>
    </>
  )
}
